import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Button, Card, Typography, InputNumber, Space, Popover, Select, Spin } from 'antd';
import { LeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import EditShiftBat from '../ShiftBat/EditShiftBat';
import useAllStops from '../../hooks/useAllStops';
import useAllTransfers from '../../hooks/useAllTransfers';
import useAllRoutes from '../../hooks/useAllRoutes';
import useAllSchedules from '../../hooks/useAllSchedules';
import useModel from '../../hooks/useModel';
import { ShiftBat } from '../../model/shiftBat';
import SaveMenu from '../../components/SaveMenu';
import LoadMessage from '../../components/LoadMessage';
import { charterModelData, deadrunModelData, shiftBatModelData } from '../../services/ModelService';
import { DATE_DAY_STRING } from '../../model/busRoute';
import { Calendar, Services, Timer, Bus, Print } from "../../components/Icons";
import ShareModal from '../../components/ShareModal';
import ShiftBatPrintModal from '../ShiftBat/ShiftBatPrintModal';
import { useAppContext } from '../../libs/contextLib';
import { keyBy, values } from 'lodash';
import useAllCharters from '../Charters/useAllCharters';
import { Charter } from '../../model/charter';
import { SyncOutlined } from '@ant-design/icons';

const { Title } = Typography;


function BookingDuty() {
  const { charterId, dutyId } = useParams();
  const { apiKey, operator } = useAppContext();
  const location = useLocation();
  const history = useHistory();
  const { allStops, setAllStops, allDepots } = useAllStops();
  const { allTransfers } = useAllTransfers();
  const { allRoutes } = useAllRoutes();
  const { allSchedules } = useAllSchedules();
  const { allCharters } = useAllCharters();
  const [printShiftBat, setPrintShiftBat] = useState(false);
  const [routes, setRoutes] = useState(null);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [isCreatingShiftBat, setIsCreatingShiftBat] = useState(false);
  const [preDepartureCheck, setPreDepartureCheck] = useState(operator?.opts?.charter?.preDepartureCheck || 600); // 10 minutes in seconds
  const [postDepartureCheck, setPostDepartureCheck] = useState(operator?.opts?.charter?.postDepartureCheck || 600); // 10 minutes in seconds
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [selectedStartDepot, setSelectedStartDepot] = useState(null);
  const [selectedEndDepot, setSelectedEndDepot] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const editFromQuery = queryParams.get('edit') === 'true';

  const {
    model: charter,
    setModel: setCharter,
  } = useModel({
    withId: charterId,
    modelService: charterModelData,
  });

  const {
    model: shiftBat,
    setModel: setShiftBat,
    initialModelRef: initialShiftBatRef,
    mode,
    setMode,
    controls,
  } = useModel({
    withId: dutyId,
    modelService: shiftBatModelData,
    initialInstance: new ShiftBat({}),
  });

  useEffect(() => {
    setRoutes(keyBy(charter?.itinerary, 'routeId'));
  }, [charter?.itinerary, setRoutes]);

  useEffect(() => {
    if (editFromQuery) {
      setMode({ edit: true });
    }
  }, [editFromQuery, setMode]);

  const handleBack = () => {
    history.push(`/bookings/${charterId}`);
  };

  const handleSave = async () => {
    try {
      await shiftBatModelData.save(shiftBat);
      await charterModelData.save({
        ...charter,
        duties: charter.duties.map(d => d.shiftBatId === dutyId ? shiftBat : d)
      });
      setMode({ edit: false });
    } catch (error) {
      console.error("Failed to save shiftbat:", error);
    }
  };

  const recreateShiftBat = async () => {
    if (!allStops || !allDepots) {
      return;
    }
    setIsCreatingShiftBat(true);
    try {
      const startDepot = selectedStartDepot && allDepots ? allDepots[selectedStartDepot] : null;
      const endDepot = selectedEndDepot && allDepots ? allDepots[selectedEndDepot] : null;
      const routes = charter?.itinerary?.filter(route => selectedRoutes.includes(route.routeId));
      const duty = await ShiftBat.fromCharterItinerary({
        charter,
        depot: startDepot,
        endDepot: endDepot,
        routes: routes,
        allStops, allRoutes, allTransfers,
        deadrunModelData: deadrunModelData,
        preDepartureCheck,
        postDepartureCheck
      });
      duty.shiftBatId = dutyId;
      duty.effectiveDate = charter.dates[0] || charter.startDate;
      duty.dates = charter.dates;
      duty.scheduleIds = charter.scheduleId ? [charter.scheduleId] : [];
      duty.vehicleType = charter.vehicleTypes?.[0] || null;

      setCharter(charter => new Charter({ ...charter, duties: charter.duties.map(d => d.shiftBatId === dutyId ? duty : d) }));
      await shiftBatModelData.save(duty);
      setShiftBat(duty);
    } finally {
      setIsCreatingShiftBat(false);
    }
  };

  const handleRecreate = () => {
    setIsPopoverVisible(false);
    recreateShiftBat();
  };

  const recreatePopoverContent = useMemo(() => {
    return (<Space direction="vertical" style={{ width: '100%' }}>
      <div>
        <label>Pre-Trip Check (minutes):</label>
        <InputNumber
          min={0}
          max={60}
          value={preDepartureCheck / 60}
          onChange={(value) => setPreDepartureCheck(value * 60)}
          style={{ width: '100%' }}
        />
      </div>
      {allDepots && (
        <div>
          <label>Start Depot:</label>
          <Select
            options={values(allDepots)?.map(depot => ({ label: depot.stopName, value: depot.stopId }))}
            value={selectedStartDepot}
            onChange={(value) => setSelectedStartDepot(value)}
            style={{ width: '100%' }}
            allowClear
          />
        </div>
      )}
      <div>
        {console.log('routes', charter?.itinerary)}
        <label>Charters:</label>
        <Select
          options={charter?.itinerary?.map(route => ({ label: route.routeName, value: route.routeId }))}
          value={selectedRoutes}
          onChange={(value) => setSelectedRoutes(value)}
          mode="multiple"
          style={{ width: '100%' }}
        />
      </div>
      {allDepots && (
        <div>
          <label>End Depot:</label>
          <Select
            options={values(allDepots)?.map(depot => ({ label: depot.stopName, value: depot.stopId }))}
            value={selectedEndDepot}
            onChange={(value) => setSelectedEndDepot(value)}
            style={{ width: '100%' }}
            allowClear
          />
        </div>
      )}
      <div>
        <label>Post-Trip Check (minutes):</label>
        <InputNumber
          min={0}
          max={60}
          value={postDepartureCheck / 60}
          onChange={(value) => setPostDepartureCheck(value * 60)}
          style={{ width: '100%' }}
        />
      </div>
      <Button
        type="primary"
        onClick={handleRecreate}
        disabled={isCreatingShiftBat}
        block
      >
        Generate
      </Button>
    </Space>
    );
  }, [charter, allDepots, isCreatingShiftBat, selectedRoutes, selectedStartDepot, selectedEndDepot, preDepartureCheck, postDepartureCheck]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}hr ${minutes}m`;
  };

  const formatDistance = (meters) => {
    return `${(meters / 1000).toFixed(2)}kms`;
  };

  return (
    <div className="duty-details w-secondary-menu">
      {printShiftBat ? <ShiftBatPrintModal
        allStops={allStops}
        allCharters={allCharters}
        allRoutes={routes}
        apiKey={apiKey}
        shiftBat={shiftBat}
        visible={printShiftBat}
        schedules={allSchedules}
        setVisible={setPrintShiftBat}
        charter={charter}
      /> : <></>}
      {shiftBat ? (
        <>
          <SaveMenu
            save={handleSave}
            editMode={mode.edit}
            id={shiftBat.shiftBatId}
            setEditMode={(edit) => setMode({ edit })}
            controls={controls}
            updated={controls.canUndo}
            modelInstance={shiftBat}
            initialInstanceRef={initialShiftBatRef}
            listUrl={`/bookings/${charterId}?${editFromQuery ? 'edit=true' : ''}`}
            modelService={shiftBatModelData}
            closeView={true}
            viewControls={!mode.edit && shiftBat && <>
              <Button type="primary" className="icon-button btn-filled mr-2" icon={<Print />}
                disabled={!shiftBat}
                onClick={async event => {
                  setPrintShiftBat(true);
                }}>Print</Button>
              <ShareModal shiftBat={shiftBat} />
            </>}
            editControls={mode.edit && charter?.itinerary?.length ?
              <Popover
                content={recreatePopoverContent}
                title="Configure Check Times"
                trigger="click"
                placement="bottomRight"
                open={isPopoverVisible}
                onOpenChange={setIsPopoverVisible}
              >
                <Button
                  disabled={isCreatingShiftBat}
                  type="primary"
                  className="icon-button btn-filled"
                  icon={<SyncOutlined spin={isCreatingShiftBat} />}
                >
                  Generate
                </Button>
              </Popover> : <></>}
          />
          <Card className="card-main no-pad d-flex">
            {/* <Row gutter={[20, 20]}>
              <Col xs={16} style={{ padding: '15px 0 15px 30px' }}>
                <Title level={4} style={{ fontSize: 18 }}>Duty Details - {shiftBat.shiftBatName}</Title>
                <Row gutter={[20, 0]}>
                  <Col xs={12} lg={4} className="d-flex align-items-center">
                    <div className="icon-info-text">
                      <Calendar />
                      <div className="w-100">
                        <strong>{charter?.dates?.map(date => date?.format("DD/MM/YYYY") + ", ")}</strong>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} lg={4} className="d-flex align-items-center">
                    <div className="icon-info-text">
                      <Services />
                      <div className="w-100">
                        <strong>{formatDistance(shiftBat.getShiftDistance())}</strong>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} lg={4} className="d-flex align-items-center">
                    <div className="icon-info-text">
                      <Timer />
                      <div className="w-100">
                        <strong>{formatTime(shiftBat.getShiftTime())}</strong>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} lg={8} className="d-flex align-items-center">
                    <div className="icon-info-text">
                      <Bus />
                      <div className="w-100">
                        <strong>{shiftBat.vehicleType?.vehicleTypeName}</strong>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col xs={8} style={{ textAlign: 'right', padding: '15px 20px 0 0' }} className='dark-buttons-wrap'>
              {!mode.edit && shiftBat && <>
                <Button type="primary" className="icon-button btn-filled mr-2" icon={<Print />}
                  disabled={!shiftBat}
                  onClick={async event => {
                    setPrintShiftBat(true);
                  }}>Print</Button>
                <ShareModal shiftBat={shiftBat} />
              </>}
              {mode.edit && charter.itinerary?.length ?
                <Popover
                    content={recreatePopoverContent}
                    title="Configure Check Times"
                    trigger="click"
                    placement="bottomRight"
                    open={isPopoverVisible}
                    onOpenChange={setIsPopoverVisible}
                >
                    <Button
                            disabled={isCreatingShiftBat}
                            type="primary"
                            className="icon-button"
                            icon={<SyncOutlined spin={isCreatingShiftBat}/>}
                    >
                        Recreate
                    </Button>
                </Popover> : <></>}
              </Col>
            </Row> */}
            <EditShiftBat
                charter={true}
                shiftBat={shiftBat}
                initialEditMode={mode.edit}
                setShiftBatHistory={newShift => {
                  if (typeof newShift === 'function') {
                    newShift = newShift(shiftBat);
                  }
                  setShiftBat(newShift);
                }}
                allStops={allStops}
                allTransfers={allTransfers}
                allRoutes={keyBy(charter?.itinerary, 'routeId')}
                setAllStops={setAllStops}
                allSchedules={allSchedules}
                isLoading={isCreatingShiftBat}
              />
          </Card>
        </>
      ) : (
        <LoadMessage message="Loading Duty Details..." />
      )}
    </div>
  );
}

export default BookingDuty; 